// Per-page metadata only. Site-wide values (robots, referrer, format-detection,
// facebook-domain-verification) are set on the root layout — do not duplicate
// them here.
export interface PageMetadata {
  documentTitle: string;
  metaTitle: string;
  description: string;
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface ImageRef {
  src: string;
  sourceSrc: string;
  alt: string;
  width: number;
  height: number;
}

export interface TextSegment {
  type: 'text';
  text: string;
}

export interface LinkSegment {
  type: 'link';
  text: string;
  href: string;
}

export type RichTextSegment = TextSegment | LinkSegment;

export interface HeroContent {
  blockId: string;
  title: string;
  titleAccent: string;
  subtitle: string;
  reviewHref: string;
  ratingText: string;
  dotBackground: string;
  formAnchorHref: string;
}

export interface LogoStripContent {
  blockId: string;
  images: ImageRef[];
}

export interface IntroContent {
  blockId: string;
  eyebrow: string;
  title: string;
  body: RichTextSegment[];
}

export interface DecoderContent {
  blockId: string;
  eyebrow: string;
  title: string;
  body: string;
  image: ImageRef;
}

export type BenefitIcon =
  | 'lock'
  | 'eye'
  | 'checkbox'
  | 'delivery'
  | 'ship'
  | 'clipboard'
  | 'coin'
  | 'certificate';

export interface BenefitCard {
  title: string;
  icon: BenefitIcon;
  body: RichTextSegment[];
}

export interface BenefitsContent {
  blockId: string;
  title: string;
  cards: BenefitCard[];
}

export interface LayeredTab {
  id: string;
  label: string;
  href: string;
  heading: string;
  secondaryHeading?: string;
  bullets: string[];
  secondaryBullets?: string[];
  image: ImageRef;
  hoverImage?: ImageRef;
  viewMoreHref?: string;
}

export interface LayeredSecurityContent {
  blockId: string;
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  sourceInteraction: 'tabs';
  tabs: LayeredTab[];
}

export interface GalleryCard {
  title: string;
  subtitle: string;
  href: string;
  image: ImageRef;
}

export interface GalleryContent {
  blockId: string;
  title: string;
  body: string;
  sourceInteraction: 'slick.gallery_active';
  cards: GalleryCard[];
}

export type ResourceIcon = 'notepad' | 'map' | 'book' | 'video';

export interface ResourceCard {
  title: string;
  icon: ResourceIcon;
  body: RichTextSegment[];
}

export interface ResourcesContent {
  blockId: string;
  title: string;
  paragraphs: string[];
  cta: {
    label: string;
    href: string;
  };
  cards: ResourceCard[];
}

export interface ChooseTab {
  id: string;
  label: string;
  href: string;
  heading: string;
  bullets: string[];
  image: ImageRef;
}

export interface ChooseScwContent {
  blockId: string;
  eyebrow: string;
  title: string;
  body: string;
  sourceInteraction: 'tabs';
  tabs: ChooseTab[];
}

export interface FinalQuoteContent {
  blockId: string;
  id: string;
  eyebrow: string;
  title: string;
  subheading: string;
  body: string;
  backgroundImage: string;
  hubspotTitle: string;
  emptyFormMethod: 'get';
}

const image = (
  sourceSrc: string,
  alt: string,
  width = 240,
  height = 140,
): ImageRef => ({
  src: sourceSrc.startsWith('https://') ? sourceSrc : `https://scw-commerce-media.s3.amazonaws.com${sourceSrc}`,
  sourceSrc,
  alt,
  width,
  height,
});

export const metadata: PageMetadata = {
  documentTitle:
    'Logistics & Warehouse Security Solutions - Surveillance, Access Control & AI-Analytics',
  metaTitle:
    'Logistics & Warehouse Security Solutions  - Surveillance, Access Control & AI-Analytics',
  description:
    'Smarter security for Logistics starts now. SCW can protect your operation and workforce with money-saving time and benefits.  Learn more \n',
};

export const breadcrumbs: BreadcrumbItem[] = [
  { label: 'Home', href: 'https://www.getscw.com/' },
  { label: 'Logistics' },
];

export const hero: HeroContent = {
  blockId: 'industries-logistics.hero',
  title: 'Security Technology for Logistics',
  titleAccent: 'Logistics',
  subtitle:
    'Smarter Security, Better Safety Compliance, and Reduced Operational Costs for Logistics, Warehousing, Distribution, and Fleets Starts Now',
  reviewHref: 'https://birdeye.com/scw-155622160539842',
  ratingText: 'Rated 4.9/5 by thousands of businesses',
  dotBackground: 'https://scw-commerce-media.s3.amazonaws.com/images/industries/healthcare/header_orange_dot_bg.svg',
  formAnchorHref: '#',
};

export const heroLogos: LogoStripContent = {
  blockId: 'industries-logistics.heroLogos',
  images: [
    image('/images/logos/clients/logistics/amazon.png', 'Amazon Fulfilment', 180, 90),
    image('/images/logos/clients/logistics/UPS.png', 'UPS', 180, 90),
    image('/images/logos/clients/food-production/us-foods.png', 'US Foods', 180, 90),
    image('/images/logos/clients/logistics/ard-logistics.png', 'Ard Logisitcs', 180, 90),
    image('/images/logos/clients/logistics/pacific-cascade.png', 'Pacific Cascade', 180, 90),
    image('/images/logos/clients/logistics/internaltional-trade-logistics.png', 'International Trade Logisitcs', 180, 90),
  ],
};

export const challenges: IntroContent = {
  blockId: 'industries-logistics.challenges',
  eyebrow: 'Security and Operational ROI you can Track',
  title: 'Logistics and Warehousing Facilities Security Challenges:',
  body: [
    {
      type: 'text',
      text: 'From safety and compliance in the warehouse to ',
    },
    {
      type: 'link',
      text: 'catalytic converter theft',
      href: 'https://www.getscw.com/knowledge-base/preventing-catalytic-converter-theft',
    },
    {
      type: 'text',
      text:
        ", diesel siphoning, and drivers damaging the vehicles in the parking lot, Logistics companies have a lot of balls to juggle. In addition, theft, break-ins, and disputes over when pallets have been loaded/shipped out, doesn't just create loss of inventory, but can also completely slow or shut down operations, causing significant harm. For warehousing and logistics companies, controlling access and having eyes on inventory and fleets is a must.",
    },
  ],
};

export const decoder: DecoderContent = {
  blockId: 'industries-logistics.decoder',
  eyebrow: 'Integrating Surveillance Video with Application Data',
  title: 'Logistics Facility Operational Challenges:',
  body:
    "You don't just need to keep an eye on video feeds, you also need to check your ERP, your production statistics, upcoming shipments, and staff schedules. Don't worry, we've got you covered. If it can be displayed on a computer screen or digital television, we can record it and display it on your viewing stations. (A Oracle Netsuite integration is shown in image, but we can do this for any application interface).",
  image: image('/images/decoding/decoder-input-oracle-netsuite.png', '', 920, 500),
};

export const benefits: BenefitsContent = {
  blockId: 'industries-logistics.benefits',
  title: 'Key Benefits for SCW Logistics Clients',
  cards: [
    {
      title: 'Restrict Access to Authorized Personnel',
      icon: 'lock',
      body: [
        { type: 'text', text: 'Easily give employees access to building areas on a need-to-access basis. Limit access by time of day and/or day of week. ' },
        { type: 'link', text: 'learn more', href: '/access-control/overview' },
      ],
    },
    {
      title: 'Watch your Perimeter',
      icon: 'eye',
      body: [
        { type: 'text', text: 'Long range cameras enable you to detect unwanted or unknown individuals before they cause an incident, as well as reduce theft and vandalism. ' },
        { type: 'link', text: 'more info', href: '/knowledge-base/long-range-security-camera' },
      ],
    },
    {
      title: 'Check for Safety Compliance',
      icon: 'checkbox',
      body: [
        { type: 'text', text: 'Management can be alerted when safety rules are not followed, such as our hard hat detection analytic ' },
        { type: 'link', text: 'more info', href: '/survail/computer-vision/model-zoo/person-detection/hardhat' },
      ],
    },
    {
      title: 'Protect Employee and Fleet Parking',
      icon: 'delivery',
      body: [
        { type: 'text', text: 'Logistics companies have a lot of employees who need to park their vehicles and a lot of vehicles that need parking. Discourage and track catalytic converter theft, diesel siphoning, or drivers damaging the vehicles.' },
      ],
    },
    {
      title: 'Solve Disputes over Ship Status',
      icon: 'ship',
      body: [
        { type: 'text', text: 'Nothing wastes time like a customer who says "you didn\'t ship that pallet that you said you shipped to me." Now you can visually check.' },
      ],
    },
    {
      title: 'Keep Track of Incident Reports',
      icon: 'clipboard',
      body: [
        { type: 'text', text: 'Solve your evidence tracking and chain of custody problems by utilizing survail vault cloud storage and incident reporting tools. ' },
        { type: 'link', text: 'learn more', href: '/survail/vsaas-vms#vault' },
      ],
    },
    {
      title: 'Reduce Operational costs by using Remote Guards',
      icon: 'coin',
      body: [
        { type: 'text', text: 'Using our visual verification service will save you thousands, as compared to a 24/7 security guard.' },
      ],
    },
    {
      title: 'Improve Network Cyber Security',
      icon: 'certificate',
      body: [
        { type: 'text', text: 'Our cloud based platforms increase your cyber security posture and make your security systems easier to manage.' },
      ],
    },
  ],
};

export const midLogos: LogoStripContent = {
  blockId: 'industries-logistics.midLogos',
  images: [
    image('/images/logos/clients/logistics/hollingsworth.png', 'Hollingsworth', 180, 90),
    image('/images/logos/clients/logistics/18-wheels-ca.png', '18 wheels canada', 180, 90),
    image('/images/logos/clients/manufacturing/thyssenkrupp.png', 'ThyssenKrupp', 180, 90),
    image('/images/logos/clients/logistics/lewis-logisics.png', 'Lewis Logistics', 180, 90),
    image('/images/logos/clients/manufacturing/international-paper.png', 'International Paper', 180, 90),
    image('/images/logos/clients/logistics/sovana.png', 'Sovana', 180, 90),
  ],
};

export const layeredSecurity: LayeredSecurityContent = {
  blockId: 'industries-logistics.layeredSecurity',
  id: 'layered',
  eyebrow: 'The key to Safety and Security for Logistics Companies:',
  title: 'Layered Security',
  body:
    'Good security has multiple layers that solve different and usually escalating needs. Different incidents have different levels of risk and more severe threats should trigger multiple systems.',
  sourceInteraction: 'tabs',
  tabs: [
    {
      id: 'truancy',
      label: 'Monitor Loading Docks',
      href: '#truancy',
      heading: 'Monitor Loading Dock',
      bullets: [
        'Cameras strategically placed to watch the loading dock area, both for packing and parking.',
        'Cameras facing the packing area can be used by to verify what was packed and which truck it was packed onto.',
        'Cameras facing the trucks can be used to check for damage and defects.',
        'The blue zones indicate the views of strategically placed varifocal lens long range cameras.',
      ],
      image: image('/images/industries/logistics/loading-dock-floorplan.png', 'loading dock', 820, 460),
      hoverImage: image('/images/industries/logistics/loading-dock-camera-view.jpeg', '', 680, 380),
      viewMoreHref: '#',
    },
    {
      id: 'lot-intrusion',
      label: 'Lot Intrusion',
      href: '#lot-intrusion',
      heading: 'Parking Lot Entrance Intrusion',
      bullets: [
        'Cameras strategically placed to watch exits and entrances help prevent and alert your security teams to vehicles entering or exiting the property.',
        'Often these cameras require wireless backhauls, which we will cover on the "network access + security" layer.',
        'The red zones indicate the views of strategically placed, varifocal lens zoomed in cameras with low light capabilities. This enables you to capture great quality images of license plates, even in extremely challenging lighting.',
        'Most logistics companies do not have fences around their property, which means that an intruder may enter from any direction, but the odds are that the vehicle will enter or leave via the entrances, so this is the best place to start. Full parking lot coverage is shown in the "monitoring fleet parking" layer.',
        'The camera setups for entrances use cameras facing towards and away from the entrance. This is preferred because not all states require license plates on both front and back of vehicles. Single cameras can be passable in states that require front plates.',
      ],
      image: image('/images/industries/dealerships/floorplans/lpr-entrances.png', 'lpr entrances', 820, 460),
      hoverImage: image('/images/industries/dealerships/floorplans/archer.gif', '', 680, 380),
      viewMoreHref: '#',
    },
    {
      id: 'parking',
      label: 'Monitor Fleet Parking',
      href: '#parking',
      heading: 'Monitor Fleet Parking',
      bullets: [
        'Catalytic converter theft, diesel siphoning, and vandalism to vehicles can drastically alter service levels and shipment timelines',
        'Often these cameras require wireless backhauls, which we will cover on the "network access + security" layer.',
        'Cameras strategically placed to watch parking areas and parking entry points.',
        'The blue zones indicate the views of strategically places varifocal lens long range cameras.',
      ],
      image: image('/images/industries/logistics/fleet-parking.png', 'fleet parking', 820, 460),
      hoverImage: image('/images/industries/manufacturing/process-camera.png', '', 680, 380),
      viewMoreHref: '#',
    },
    {
      id: 'liability',
      label: 'Safety & Inventory Check',
      href: '#liability',
      heading: 'Liability and Risk Reduction: Safety Check',
      bullets: [
        'Suit up! Cameras placed in this zone help assure that all personnel are following property safety equipment protocols before entering the production line.',
        'The green zones indicate the views of strategically places wide angle lens cameras for looking down warehouse aisles.',
      ],
      image: image('/images/floorplan-samples/warehouse-complex.jpg', 'camera placement for safety gear compliance use case', 820, 460),
      hoverImage: image('/images/industries/logistics/warehouse.jpeg', '', 680, 380),
    },
    {
      id: 'access',
      label: 'Unauthorized Access',
      href: '#access',
      heading: 'Unauthorized Access',
      bullets: [
        'Electronic door locks and bluetooth readers allow you to control access to sensitive areas.',
        "Administrators can create groups based on job function. For example, office workers could only be given access to enter into their spaces, as seen on the image with the black readers, while warehouse staff could be be given access to enter the warehouse area, as seen with the white readers. Meanwhile, management's credentials could bet setup to allow entry to either space.",
      ],
      image: image('/images/industries/manufacturing/control-access.png', 'control access', 820, 460),
      hoverImage: image('/images/industries/education/floorplans/wave-to-unlock-large.gif', '', 680, 380),
    },
    {
      id: 'intrusion',
      label: 'Warehouse Intrusion',
      href: '#intrusion',
      heading: 'Warehouse Intrusion',
      bullets: [
        'Cameras strategically placed in outdoors in areas with entry points, such as doors and windows, can alert your security teams to unwelcome individuals and unauthorized access.',
        'The blue zones indicate the views of strategically places fixed lens wide angle cameras.',
        'Long range camera are often also deployed to watch remote assets like gates or ancillary structures',
      ],
      image: image('/images/industries/manufacturing/intrusion.png', 'intrusion', 820, 460),
      hoverImage: image('/images/industries/manufacturing/intrusion-camera.png', '', 680, 380),
    },
    {
      id: 'emergency',
      label: 'Emergency',
      href: '#emergency',
      heading: 'Emergency',
      bullets: [
        'Administrators can remotely lock down their institution in a variety of emergencies, including active shooter, natural disasters, and unsolicited visitor situations.',
      ],
      image: image('/images/industries/education/floorplans/lockdown-zones-large.webp', 'lockdown zones', 820, 460),
      hoverImage: image('/images/industries/education/floorplans/lockdown-app.jpeg', '', 680, 380),
    },
    {
      id: 'network-security',
      label: 'Network Access + Security',
      href: '#network-security',
      heading: 'Wireless Backhauls',
      bullets: [
        "Dealerships often have multiple buildings and expansive parking lots. That's a lot of area to cover. Often this requires installing wireless backhauls to connect disparate buildings, or to enable network connectivity for cameras placed on poles throughout the parking areas.",
        'Point-to-point wireless, military-grade beam wifi is often used to transport data between different areas on the property. These wireless beams are shown on the map in green.',
      ],
      secondaryHeading: 'Network, Cyber, and Application Security',
      secondaryBullets: [
        'Our platforms use encryption, inbound connection refusal, automatic software updates, and cloud-based management so your physical security devices are not compromising your cyber security initiatives.',
        'VPN, MFA, SOC/SIEM, and Endpoint management services also available.',
      ],
      image: image('/images/industries/dealerships/floorplans/wireless-backhaul.png', 'wireless backhauls', 820, 460),
    },
  ],
};

export const postLayeredLogos: LogoStripContent = {
  blockId: 'industries-logistics.postLayeredLogos',
  images: [
    image('/images/logos/clients/logistics/flex-logistics.png', 'Flex Logistics', 180, 90),
    image('/images/logos/clients/manufacturing/Caterpillar.png', 'Caterpillar', 180, 90),
    image('/images/logos/clients/manufacturing/Boise_Cascade.png', 'Boise Cascade', 180, 90),
    image('/images/logos/clients/manufacturing/woodgrain.png', 'Woodgrain', 180, 90),
    image('/images/logos/clients/logistics/AFG.png', 'AFG', 180, 90),
    image('/images/logos/clients/manufacturing/General_Electric.png', 'General Electric', 180, 90),
  ],
};

export const hardwareSolutions: GalleryContent = {
  blockId: 'industries-logistics.hardware',
  title: 'Hardware and Software Solutions',
  body: 'These are the tools that you want to implement to secure your warehouse and shipping facility.',
  sourceInteraction: 'slick.gallery_active',
  cards: [
    { title: 'Cloud Based', subtitle: 'Access Control', href: '/access-control/software', image: image('/images/OpenPath/versatile-deployment-options.png', 'gallery', 360, 280) },
    { title: 'Survail', subtitle: 'ICVR', href: '/icvr', image: image('/images/products/SAI4000532.jpg', 'gallery', 360, 280) },
    { title: 'Long Range', subtitle: 'Low Light Cameras', href: '/knowledge-base/long-range-security-camera', image: image('/images/products/26BV2-L.jpg', 'gallery', 360, 280) },
    { title: 'Survail', subtitle: 'Platform', href: '/survail/vsaas-vms', image: image('/images/industries/education/survail-platform-square.png', 'gallery', 360, 280) },
    { title: 'Wireless', subtitle: 'Backhauls', href: '/cctv-accessories/ip-wireless', image: image('/images/products/ubiquiti-nanobeam-ac-gen2-nbe-5ac-gen2-left-angle.png', 'gallery', 360, 280) },
  ],
};

export const services: GalleryContent = {
  blockId: 'industries-logistics.services',
  title: 'Security Services For Warehouses, Logistics & Distribution Centers',
  body:
    'Many warehouses choose to layer on additional services such as professional monitoring to help with maintenance and operations. SCW specializes in warehouse security system design, CCTV, and camera installation, ensuring comprehensive protection for logistics, warehousing and distribution centers',
  sourceInteraction: 'slick.gallery_active',
  cards: [
    { title: 'Installation', subtitle: 'We can do it for you', href: '/services', image: image('/images/products/installation.jpg', 'gallery', 360, 280) },
    { title: 'Visual Verification', subtitle: 'Pro Monitoring', href: '/services/professional-camera-monitoring', image: image('/images/products/remote-monitoring.jpg', 'gallery', 360, 280) },
    { title: 'Service Plans', subtitle: 'Maintenance', href: '/services/service-contract.php', image: image('/images/products/service-plan.jpg', 'gallery', 360, 280) },
    { title: 'Floorplan', subtitle: 'Service', href: '/free-security-camera-floorplan', image: image('/images/industries/retail/cashwrap.png', 'gallery', 360, 280) },
    { title: 'Cloud Storage', subtitle: 'Dual Retention', href: '/survail/cloud', image: image('/images/products/cloud-storage.png', 'gallery', 360, 280) },
  ],
};

export const lowerLogos: LogoStripContent = {
  blockId: 'industries-logistics.lowerLogos',
  images: [
    image('/images/logos/clients/conglomerate/3M.png', '3M', 180, 90),
    image('/images/logos/clients/conglomerate/Honeywell.png', 'Honeywell', 180, 90),
    image('/images/logos/clients/manufacturing/john-manville.png', 'John Manville', 180, 90),
    image('/images/logos/clients/manufacturing/nucor.png', 'Nucor', 180, 90),
    image('/images/logos/clients/conglomerate/nike.png', 'Nike', 180, 90),
    image('/images/logos/clients/manufacturing/weyerhaeuser.png', 'Weyerhaeuser', 180, 90),
  ],
};

export const resources: ResourcesContent = {
  blockId: 'industries-logistics.resources',
  title: 'Resources for Logistics',
  paragraphs: [
    'A warehouse or logistics company has many security is a challenge -- from safety compliance, to risks about vehicle theft or vandalism, to employees having accidents, to theft or damage to inventory.',
    'SCW makes it possible to bring all the moving parts together to run a well-oiled machine.',
    'Here are some case studies, guides, and videos that help you make the right choice.',
  ],
  cta: { label: 'Get in Touch', href: '#sign-up' },
  cards: [
    {
      title: 'Logistics',
      icon: 'notepad',
      body: [
        { type: 'link', text: 'AFG Distribution', href: '/downloads/case-studies/SCW-Case-Study-AFG-Distribution.pdf' },
        { type: 'text', text: ' brought SCW into their new distribution center construction to get coverage for the entire interior of the facility, exterior access control, and a communication system to assist drivers at the gate.' },
      ],
    },
    {
      title: 'Coverage Map',
      icon: 'map',
      body: [
        { type: 'text', text: 'Need inspiration? Check our this ' },
        { type: 'link', text: 'Sample Security Camera System Coverage Map', href: '#layered' },
        { type: 'text', text: ' for a manufacturing facility.' },
      ],
    },
    {
      title: 'Guide: NDAA',
      icon: 'book',
      body: [
        { type: 'text', text: 'All SCW camera are compliant with the National Defense Authorization Act. In this ' },
        { type: 'link', text: 'NDAA guide', href: '/knowledge-base/the-ban' },
        { type: 'text', text: ', we talk about what the NDAA regulations mean to the security industry.' },
      ],
    },
    {
      title: 'Webinar on Demand',
      icon: 'video',
      body: [
        { type: 'link', text: 'Enhance Your Process Monitoring', href: 'https://www.getscw.com/process-monitoring-webinar' },
        { type: 'text', text: ': learn how to minimize latency and improve video quality for process monitoring systems, using real-world examples demonstrating successful implementations.' },
      ],
    },
    {
      title: 'Case Study',
      icon: 'book',
      body: [
        { type: 'link', text: 'Emery Air', href: 'https://www.getscw.com/case-study-viewer?casestudy=Emery%20Air' },
        { type: 'text', text: ' picked SCW because they needed an NDAA compliant solution for live viewing their cargo warehouse, logistics, and flight schools.' },
      ],
    },
    {
      title: 'Video: Roundtable Discussion',
      icon: 'video',
      body: [
        { type: 'text', text: 'In this episode of "In the Trenches: Roundtable," we discuss ' },
        { type: 'link', text: 'mounting security cameras to ceilings', href: '/installation-portal/mounting-security-cameras-to-ceilings-discussion' },
        { type: 'text', text: '.' },
      ],
    },
  ],
};

export const chooseScw: ChooseScwContent = {
  blockId: 'industries-logistics.chooseScw',
  eyebrow: 'Why Logistics Companies',
  title: 'Choose SCW',
  body: 'We have the history and expertise your company needs.',
  sourceInteraction: 'tabs',
  tabs: [
    {
      id: 'it-team',
      label: 'IT Team',
      href: '#it-team',
      heading: 'For Your IT Team',
      bullets: [
        'Openpath Access Control System is a simple retrofit installation that is easily programmable and maintenance-free.',
        'SCW cameras are a single cable plug-and-play item that can stretch up tp 750 feet in you combine them with one of our extended transmission POE Switches.',
        "Both the Survail and Openpath Platforms include free and ongoing training and support. You don't have to do this.",
        "The Survail Platforms include one-on-one onboarding of devices, networks, and users, before we even ship it to you. There's no complicated setup process.",
        'The Survail Platform has integrated VLANs and VPNs which reduce network complexity and reduce the IT time spent on device management and securing remote access, when compared to on-premises NVR-based systems.',
        'The Survail and Openpath platforms Increase your company’s cybersecurity posture and decrease your cyber threat footprint, when compared to non-cloud, on-premesis systems.',
        "Every camera on one manufacturer-agnostic platform makes employee training infinitely simpler. The UI is so easy to use that other teams don't have to bother IT with every footage request.",
        'The ready availability of our on-call technical experts means total support for the life of your new system.',
      ],
      image: image('/images/templates/judge_why_choose.png', 'Why Choose 1', 760, 520),
    },
    {
      id: 'cheif-security-officers',
      label: 'Managers',
      href: '#cheif-security-officers',
      heading: 'For Your Managers',
      bullets: [
        'Both the Survail and Openpath Platforms include free and ongoing training and support.',
        'Imagine no more need to stare at a screen of security cameras all day! Every camera in your system--without limit--is being analyzed to identify what matters to you most.',
        'SCW AI-powered analytics software does all the watching for you. The AI-Powered Analytics distinguish individuals and vehicles in an evolving, learning process.',
        'In less than a second, recognize a potential threat incident and notify pre-selected personnel and first responders by text message, email alert, or automatic phone call.',
        'All notifications are in real-time. No more valuable minutes are lost. Immediate alerts profoundly alter outcomes.',
        'Every camera can filter by person, vehicle, camera, date, or time, which means no more endless searching for pertinent footage.',
        'The AI-Analytics proactively eliminate false positive events and reduce your huge number of current email alerts.',
      ],
      image: image('/images/templates/judge_why_choose.png', 'Why Choose 2', 760, 520),
    },
    {
      id: 'administrators',
      label: 'Executives',
      href: '#administrators',
      heading: 'For Executives',
      bullets: [
        'The secure cloud-based Survail and Openpath platforms offer a lower total-cost-of-ownership by removing the requirements that your IT team worry about enabling and securing remote access to your on-premises hardware and network.',
        'SCW offers the benefits you want - a system that keeps your educational facility secure without further involvement on from administrators or constant budget expansions.',
        'Our professional-grade equipment is rugged and performance-driven. Our sophisticated technology is both simple to install and maintain.',
        'Each component comes with 3 to 5 year warranties and is backed by the support of our US-based technical staff for the lifetime of your security system.',
        "Our AI-Powered Analytics and easy to use reporting and retention tools mean that you won't have to hire additional staff to manage your system.",
      ],
      image: image('/images/templates/judge_why_choose.png', 'Service 3', 760, 520),
    },
  ],
};

export const finalQuote: FinalQuoteContent = {
  blockId: 'industries-logistics.finalQuote',
  id: 'sign-up',
  eyebrow: 'Get Started',
  title: 'With A Modern Surveillance Ecosystem For Your Logistics Business',
  subheading: 'Invest In The Best',
  body:
    'SCW can protect your operation and workforce with money-saving time and benefits. And your IT and Security teams will love our US-based technical support. It comes FREE. Forever. World-class customer service and technical support are central to providing you with the best security available, anywhere.',
  backgroundImage: 'https://scw-commerce-media.s3.amazonaws.com/images/industries/logistics/logistics-2-min.jpeg',
  hubspotTitle: 'Connect with the experts:',
  emptyFormMethod: 'get',
};
